/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback } from 'react';
import { PropTypes } from 'prop-types';
import get from 'lodash/get';
import { Box, Heading, RequiredValidation, Stack, Text } from 'sarsaparilla';

import { PERMIT as permitProps } from 'ui-permit/src/props/permit';
import SmartSelector from 'ui-permit/src/components/SmartSelector';
import { readRuleValue, getClassName } from 'ui-permit/src/utils/utils';
import * as types from 'ui-permit/src/constants';

import { useValidation } from '../hooks/useValidation';
import { PICKUP_LOCATION_PROP_PATH } from '../constants/propPaths';

const defaultDataBindingMap = {
  [PICKUP_LOCATION_PROP_PATH]: 'pickup_location_id'
};

const defaultAttributes = {};

export default (issuanceShape, dataBindingsMap = defaultDataBindingMap, attributes = defaultAttributes) => {
  const dm = {...defaultDataBindingMap, ...dataBindingsMap};

  const propTypes = {
    reservation: issuanceShape,
    permit: permitProps,
    pActions: PropTypes.shape({
      formValidationChange: PropTypes.func.isRequired,
      updatePendingReservation: PropTypes.func.isRequired
    }).isRequired,
    validIssueStations: PropTypes.array,
    displayValidationError: PropTypes.bool.isRequired,
    renderIssueStationPrintingOverrideOptions: PropTypes.shape({
      enabled: PropTypes.bool,
      value: PropTypes.number
    }),
    validation: PropTypes.shape({}).isRequired,
    shouldValidateOnBEOnly: PropTypes.bool,
    isRequired: PropTypes.bool,
  };

  const defaultProps = {
    validIssueStations: [],
    renderIssueStationPrintingOverrideOptions: {
      enabled: false,
      value: 1
    },
    isRequired: false
  };

  function IssueStationDetail({
    name,
    pActions,
    permit,
    reservation,
    shouldValidateOnBEOnly,
    validIssueStations,
    validation,
    displayValidationError,
    renderIssueStationPrintingOverrideOptions,
    addFieldRef,
    isRequired
  }) {
    const selectedStationIdValue = get(reservation, dm[PICKUP_LOCATION_PROP_PATH], '');
    const controlLabel ='Issuing Station';

    const { isInvalid } = useValidation(
      { value: selectedStationIdValue, id: 'issue-station' },
      `${controlLabel} is required.`,
      addFieldRef?.(name, 0)
    );

    const handleIssueStationChange = useCallback((selectedStationId) => {
      pActions.updatePendingReservation(reservation.id, dm[PICKUP_LOCATION_PROP_PATH], selectedStationId);
      if(shouldValidateOnBEOnly)
        pActions.formValidationChange(dm[PICKUP_LOCATION_PROP_PATH], null);
    }, [pActions, reservation.id, shouldValidateOnBEOnly]);

    const hasSingleOption = validIssueStations?.length === 1;
    const firstOptionId = validIssueStations?.[0]?.id;
    const currentIssueStation = reservation[dm[PICKUP_LOCATION_PROP_PATH]];

    // if there is just 1 option and the reservation isn't set with this value, then set it
    if(hasSingleOption && currentIssueStation !== firstOptionId) {
      handleIssueStationChange(firstOptionId);
    }

    // TODO When logic is changed to only work with BE validations, deprecate/remove this function
    const onFormValidate = (isInValid, error, path) => {
      if (isInValid) {
        pActions.formValidationChange(`${reservation.id}_${path}`, error);
      }
      else {
        pActions.formValidationChange(`${reservation.id}_${path}`, null);
      }
    }

    const handleChange = ({ target: { value: selectedStationId } }) => handleIssueStationChange(selectedStationId);

    const renderSingleIssueStation = () => {
      const issueStationName = validIssueStations?.[0]?.name ?? '';
      return (
        <Stack space="sm">
          <Heading headingLevel={2} appearance="h5">
            Issuing Station
          </Heading>
          <Text>
            You will be required to pick up your permit in person at the below location. Your confirmation email will have further instructions.
          </Text>
          <Box>
            <Text fontWeight="bold">Station Location</Text>
            <Text id="issue-station">{issueStationName}</Text>
          </Box>
        </Stack>
      );
    }

    const renderPermitPickupOption = () => {
      const className = shouldValidateOnBEOnly
        ? getClassName(get(validation, `${dm[PICKUP_LOCATION_PROP_PATH]}`), true)
        : getClassName(get(validation, `${reservation.id}_${dm[PICKUP_LOCATION_PROP_PATH]}`), displayValidationError);

      const hasValidationBEError = shouldValidateOnBEOnly && get(validation, dm[PICKUP_LOCATION_PROP_PATH]);
      const hasValidationFEError = displayValidationError && selectedStationIdValue === '';
      const classNameError = hasValidationBEError || isInvalid || hasValidationFEError ? 'rec-error' : '';

      return (
        <div>
          <Heading headingLevel={2} appearance="h5" className="mb-2">
            Issuing Station
          </Heading>
          <p>
            Please select from the locations below where you will be picking up your permit.
          </p>
          <div className="rec-nested-wrap">
            <div className="rec-form-item-wrap">
              <div className="rec-grid-6-12">
                <SmartSelector
                  label="Station Location"
                  options={validIssueStations.map((a) => ({ value: a.id, label: a.name }))}
                  name="issue-station"
                  id="issue-station"
                  isRequired={isRequired}
                  value={selectedStationIdValue}
                  handleChange={handleChange}
                  className={className}
                  includePlaceholderOption
                  validationError={classNameError} />
                {!shouldValidateOnBEOnly && <RequiredValidation
                  value={selectedStationIdValue}
                  displayInSummary
                  onValidated={(isInValid, error) => onFormValidate(isInValid, error, dm[PICKUP_LOCATION_PROP_PATH])}
                  controlLabel={controlLabel} />}
              </div>
            </div>
          </div>
        </div>
      );
    }

    const renderPermitPrintingOption = (overrideValue) => {
      const printWindow = overrideValue || readRuleValue(types.ONLINE_PRINTING, permit.rules, '');
      let dateType = "launch";
      if(types.IS_DAYUSE_PERMIT(permit.id)) {
        dateType = "trip";
      }
      return (
        <div className="per-issue-station-form per-section-group">
          <Heading headingLevel={2} appearance="h5" className="mb-2">Permit Printing</Heading>
          <p>
            {`You are required to print the permit before going on this trip.
          You will be able to print your permit starting ${printWindow} days before your ${dateType} date.`}
          </p>
        </div>
      );
    }


    if (get(renderIssueStationPrintingOverrideOptions, 'enabled', false)) {
      return renderPermitPrintingOption(renderIssueStationPrintingOverrideOptions.value);
    }
    if (validIssueStations?.length === 1) {
      return renderSingleIssueStation();
    }
    return renderPermitPickupOption();
  }

  IssueStationDetail.propTypes = propTypes;
  IssueStationDetail.defaultProps = defaultProps;
  IssueStationDetail.attributes = attributes;
  IssueStationDetail.componentName = 'IssueStationDetail';

  return IssueStationDetail;
}
